import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, TrendingUp, ChefHat, AlertTriangle, Clock, Users,
  Package, UserCog, Target, Settings as SettingsIcon, Check, X, Flag, Brain,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { useCurrency } from '@/context/SettingsContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API_URL } from '@/lib/apiBase';

const COLOR = {
  growth: 'from-orange-500 to-red-500',
  profit: 'from-emerald-500 to-green-600',
  risk:   'from-rose-500 to-red-600',
  data:   'from-sky-500 to-blue-600',
  ai:     'from-violet-500 to-purple-600',
};

const Badge = ({ children, tone = 'data' }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white bg-gradient-to-r ${COLOR[tone] || COLOR.data}`}>
    {children}
  </span>
);

const SectionHeader = ({ icon: Icon, title, accent = 'ai', desc }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${COLOR[accent]} flex items-center justify-center shadow-lg`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <h3 className="font-heading text-xl font-bold">{title}</h3>
      {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
    </div>
  </div>
);

/* ------------ Tab 1: Today's AI Suggestions ------------ */
const SuggestionsTab = ({ outletId }) => {
  const [data, setData] = useState({ suggestions: [], generated_at: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/ai/suggestions?outlet_id=${outletId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error('Could not load suggestions');
    } finally { setLoading(false); }
  }, [outletId]);
  useEffect(() => { if (outletId) load(); }, [outletId, load]);

  const act = async (s, action) => {
    setBusy({ ...busy, [s.id]: action });
    try {
      await axios.post(`${API_URL}/api/ai/suggestion-action`,
        { suggestion_id: s.id, suggestion_type: s.type, title: s.title, action },
        { withCredentials: true });
      toast.success(`Marked ${action}`);
      setData((d) => ({ ...d, suggestions: d.suggestions.filter((x) => x.id !== s.id) }));
    } catch (e) {
      toast.error('Action failed');
    } finally { setBusy({ ...busy, [s.id]: null }); }
  };

  const toneFor = (p) => (p === 'high' ? 'risk' : p === 'medium' ? 'growth' : 'data');

  return (
    <div className="space-y-4">
      <SectionHeader icon={Sparkles} title="Today's AI Suggestions" accent="ai"
        desc="Smart recommendations based on the last 30 days of sales & inventory." />
      {loading ? <p className="text-sm text-muted-foreground">Analyzing your data…</p> : (
        data.suggestions.length === 0 ? <Card><CardContent className="p-8 text-center text-muted-foreground">No new suggestions right now. Check back tomorrow.</CardContent></Card>
        : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.suggestions.map((s) => (
              <Card key={s.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`sugg-${s.id}`}>
                <div className={`h-1.5 bg-gradient-to-r ${COLOR[toneFor(s.priority)]}`} />
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-bold text-lg leading-tight">{s.title}</h4>
                    <Badge tone={toneFor(s.priority)}>{s.priority}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{s.reason}</p>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div><p className="uppercase text-muted-foreground">Impact</p><p className="font-semibold">{s.expected_impact}</p></div>
                    <div><p className="uppercase text-muted-foreground">Confidence</p><p className="font-semibold">{Math.round((s.confidence_score || 0) * 100)}%</p></div>
                  </div>
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                    <strong>Suggested action:</strong> {s.suggested_action}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" disabled={!!busy[s.id]} onClick={() => act(s, 'approve')} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`approve-${s.id}`}>
                      <Check size={14} className="mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="outline" disabled={!!busy[s.id]} onClick={() => act(s, 'done')} data-testid={`done-${s.id}`}>
                      <Flag size={14} className="mr-1" />Done
                    </Button>
                    <Button size="sm" variant="outline" disabled={!!busy[s.id]} onClick={() => act(s, 'ignore')} className="text-rose-500" data-testid={`ignore-${s.id}`}>
                      <X size={14} className="mr-1" />Ignore
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
      )}
    </div>
  );
};

/* ------------ Tab 2: Smart Upsell Engine (read-only preview / live works in POS) ------------ */
const UpsellTab = ({ outletId }) => {
  const { menuItems } = useApp();
  const [itemId, setItemId] = useState('');
  const [list, setList] = useState([]);
  const sym = useCurrency();

  const load = async (id) => {
    if (!id) return;
    const r = await axios.get(`${API_URL}/api/ai/upsell?outlet_id=${outletId}&item_id=${id}`, { withCredentials: true });
    setList(r.data.suggestions || []);
  };
  useEffect(() => { if (itemId) load(itemId); }, [itemId, outletId]);

  return (
    <div className="space-y-4">
      <SectionHeader icon={Target} title="Smart Upsell Engine" accent="growth"
        desc="Live in POS — pick any item to preview what it triggers." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-1">
          <Label className="text-xs">Pick a menu item</Label>
          <Select value={itemId} onValueChange={setItemId}>
            <SelectTrigger data-testid="upsell-item-pick"><SelectValue placeholder="Choose item…" /></SelectTrigger>
            <SelectContent>{menuItems.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        {list.map((s) => (
          <button key={s.menu_item_id} className="group rounded-2xl px-5 py-4 text-white font-semibold shadow-lg bg-gradient-to-br from-orange-500 to-red-500 hover:scale-105 transition-transform" data-testid={`upsell-pill-${s.menu_item_id}`}>
            <div className="text-sm">{s.name}</div>
            <div className="text-xs opacity-90">+ {sym}{(s.price || 0).toFixed(2)} · {Math.round((s.confidence_score || 0) * 100)}% confidence</div>
          </button>
        ))}
        {itemId && list.length === 0 && <p className="text-sm text-muted-foreground">Not enough order history yet.</p>}
      </div>
    </div>
  );
};

/* ------------ Tab 3: Combo Recommendation ------------ */
const ComboTab = ({ outletId }) => {
  const [rows, setRows] = useState([]);
  const sym = useCurrency();
  useEffect(() => { if (outletId) axios.get(`${API_URL}/api/ai/combo-recommendations?outlet_id=${outletId}`, { withCredentials: true }).then((r) => setRows(r.data)); }, [outletId]);
  return (
    <div>
      <SectionHeader icon={ChefHat} title="Combo Recommendations" accent="profit"
        desc="Items frequently purchased together — package them into a combo." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((c) => (
          <Card key={c.id} className="overflow-hidden">
            <div className={`h-1.5 bg-gradient-to-r ${COLOR.profit}`} />
            <CardContent className="p-5 space-y-3">
              <div className="flex justify-between items-start">
                <h4 className="font-bold text-lg">{c.combo_name}</h4>
                <Badge tone="profit">{c.estimated_margin}% margin</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><p className="text-muted-foreground">Was</p><p className="font-bold line-through">{sym}{c.separate_price.toFixed(2)}</p></div>
                <div><p className="text-muted-foreground">Suggested</p><p className="font-bold text-emerald-600 text-lg">{sym}{c.suggested_price.toFixed(2)}</p></div>
                <div><p className="text-muted-foreground">Opportunity</p><p className="font-bold">{c.sales_opportunity_score}/100</p></div>
              </div>
              <Button className="w-full bg-emerald-500 text-white hover:bg-emerald-600" data-testid={`combo-create-${c.id}`}
                onClick={() => toast.success('Combo queued — admins, create in Menu → Combos')}>
                Create combo
              </Button>
            </CardContent>
          </Card>
        ))}
        {rows.length === 0 && <Card><CardContent className="p-8 text-center text-muted-foreground">Not enough co-occurrence data yet.</CardContent></Card>}
      </div>
    </div>
  );
};

/* ------------ Generic table tab helper ------------ */
const TableTab = ({ icon, title, accent, desc, columns, fetcher, deps = [], testId }) => {
  const [rows, setRows] = useState([]);
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
    fetcher()
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((err) => {
        setRows([]);
        setErrored(true);
        toast.error(`Failed to load ${title}`);
        // eslint-disable-next-line no-console
        console.error(`[AIGrowthPage] ${title} fetch failed`, err);
      });
    /* eslint-disable-next-line */
  }, deps);
  return (
    <div>
      <SectionHeader icon={icon} title={title} accent={accent} desc={desc} />
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="bg-muted/50">{columns.map((c) => <th key={c.k} className="text-left px-3 py-2 text-xs uppercase font-bold">{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t hover:bg-muted/30">
                {columns.map((c) => (
                  <td key={c.k} className="px-3 py-2">
                    {c.render ? c.render(row) : (row[c.k] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className={`px-3 py-8 text-center ${errored ? 'text-rose-500' : 'text-muted-foreground'}`}>
                {errored ? 'Failed to load — check connectivity and retry.' : 'No data yet.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
};

/* ------------ Tab 5: Peak/Slow Hours ------------ */
const PeakHoursTab = ({ outletId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (outletId) axios.get(`${API_URL}/api/ai/peak-hours?outlet_id=${outletId}`, { withCredentials: true }).then((r) => setData(r.data));
  }, [outletId]);
  const max = Math.max(1, ...(data?.hourly?.map((h) => h.orders) || [1]));
  return (
    <div>
      <SectionHeader icon={Clock} title="Peak & Slow Hour Analysis" accent="data"
        desc="When you're busiest and when to run a discount." />
      {data && (
        <Card><CardContent className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="rounded-xl bg-orange-50 border border-orange-200 p-3"><p className="text-xs uppercase text-orange-700">Peak hours</p><p className="font-bold text-lg">{data.peak_hours.map((h) => `${h}:00`).join(', ')}</p></div>
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3"><p className="text-xs uppercase text-rose-700">Slow hours</p><p className="font-bold text-lg">{data.slow_hours.map((h) => `${h}:00`).join(', ')}</p></div>
            <div className="rounded-xl bg-violet-50 border border-violet-200 p-3"><p className="text-xs uppercase text-violet-700">Best discount window</p><p className="font-bold text-lg">{data.best_discount_window || '—'}</p></div>
          </div>
          <div className="flex items-end gap-1 h-40 mt-3">
            {data.hourly.map((h) => (
              <div key={h.hour} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t bg-gradient-to-t from-sky-500 to-violet-500" style={{ height: `${(h.orders / max) * 100}%`, minHeight: '2px' }} title={`${h.hour}:00 — ${h.orders} orders`} />
                <span className="text-[9px] text-muted-foreground">{h.hour}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
};

/* ------------ Tab 10: Admin Settings ------------ */
const AIAdminSettings = ({ outletId }) => {
  const [s, setS] = useState(null);
  const load = useCallback(async () => {
    const r = await axios.get(`${API_URL}/api/ai/settings?outlet_id=${outletId}`, { withCredentials: true });
    setS(r.data);
  }, [outletId]);
  useEffect(() => { if (outletId) load(); }, [outletId, load]);
  const save = async () => {
    await axios.put(`${API_URL}/api/ai/settings?outlet_id=${outletId}`, { settings: s }, { withCredentials: true });
    toast.success('AI settings saved');
  };
  if (!s) return <p>Loading…</p>;
  const T = ({ k, label, desc }) => (
    <div className="flex items-center justify-between border-b py-3">
      <div><p className="font-medium">{label}</p>{desc && <p className="text-xs text-muted-foreground">{desc}</p>}</div>
      <Switch checked={!!s[k]} onCheckedChange={(v) => setS({ ...s, [k]: v })} data-testid={`ai-toggle-${k}`} />
    </div>
  );
  return (
    <div>
      <SectionHeader icon={SettingsIcon} title="AI Module Settings" accent="ai" desc="Control how AI suggestions surface across the app." />
      <Card><CardContent className="p-5 space-y-1">
        <T k="enabled" label="Enable AI suggestions" />
        <T k="pos_upsell_popup" label="Show upsell suggestions in POS" desc="Live add-on pills appear after each item is added" />
        <T k="combo_suggestions" label="Combo recommendations" />
        <T k="customer_coupons" label="Customer retention coupons" />
        <T k="inventory_prediction" label="Inventory prediction" />
        <T k="show_confidence" label="Show confidence scores on suggestions" />
        <div className="grid grid-cols-2 gap-3 pt-3">
          <div>
            <Label className="text-xs">Minimum sales-data window (days)</Label>
            <Select value={String(s.min_data_days)} onValueChange={(v) => setS({ ...s, min_data_days: parseInt(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{[7, 15, 30, 60].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Suggestion frequency</Label>
            <Select value={s.suggestion_frequency} onValueChange={(v) => setS({ ...s, suggestion_frequency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={save} className="mt-4 bg-gradient-to-r from-violet-500 to-purple-600 text-white" data-testid="ai-settings-save">Save settings</Button>
      </CardContent></Card>
    </div>
  );
};

/* ------------ Main page ------------ */
export const AIGrowthPage = () => {
  const { currentOutlet } = useApp();
  const sym = useCurrency();
  const oid = currentOutlet?.id;

  if (!oid) return <div className="text-center text-muted-foreground py-12">Select an outlet to view the AI Growth Engine.</div>;

  const tabs = [
    { v: 'suggestions',  label: "Today's Suggestions", icon: Sparkles },
    { v: 'upsell',       label: 'Smart Upsell',        icon: Target },
    { v: 'combo',        label: 'Combo Recommend',     icon: ChefHat },
    { v: 'low',          label: 'Low-Selling Items',   icon: AlertTriangle },
    { v: 'hours',        label: 'Peak / Slow Hours',   icon: Clock },
    { v: 'retention',    label: 'Customer Retention',  icon: Users },
    { v: 'inventory',    label: 'Inventory Predict',   icon: Package },
    { v: 'staff',        label: 'Staff Performance',   icon: UserCog },
    { v: 'profit',       label: 'Profit Optimization', icon: TrendingUp },
    { v: 'settings',     label: 'Admin Settings',      icon: SettingsIcon },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
          <Brain className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-extrabold">AI Growth Engine</h1>
          <p className="text-sm text-muted-foreground">Smart insights to grow sales, profit, and customer love.</p>
        </div>
      </div>

      <Tabs defaultValue="suggestions">
        <Card><CardContent className="p-2 overflow-x-auto">
          <TabsList className="flex-wrap h-auto bg-transparent gap-1" data-testid="ai-tabs">
            {tabs.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`ai-tab-${t.v}`}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white px-3 py-2">
                <t.icon size={14} className="mr-1.5" />{t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </CardContent></Card>

        <TabsContent value="suggestions"><SuggestionsTab outletId={oid} /></TabsContent>
        <TabsContent value="upsell"><UpsellTab outletId={oid} /></TabsContent>
        <TabsContent value="combo"><ComboTab outletId={oid} /></TabsContent>
        <TabsContent value="low">
          <TableTab icon={AlertTriangle} title="Low-Selling Items" accent="risk" testId="tbl-low"
            desc="Items underperforming over the last 30 days."
            columns={[
              { k: 'name', label: 'Item' },
              { k: 'category', label: 'Category' },
              { k: 'sales_count', label: 'Sales' },
              { k: 'revenue', label: 'Revenue', render: (r) => `${sym}${(r.revenue || 0).toFixed(2)}` },
              { k: 'margin_pct', label: 'Margin %' },
              { k: 'recommendation', label: 'AI Recommendation' },
            ]}
            fetcher={() => axios.get(`${API_URL}/api/ai/low-selling-items?outlet_id=${oid}`, { withCredentials: true }).then((r) => r.data)}
            deps={[oid]} />
        </TabsContent>
        <TabsContent value="hours"><PeakHoursTab outletId={oid} /></TabsContent>
        <TabsContent value="retention">
          <TableTab icon={Users} title="Customer Retention" accent="ai" testId="tbl-retention"
            desc="Repeat customer behaviour over the last 6 months."
            columns={[
              { k: 'customer', label: 'Customer' },
              { k: 'phone', label: 'Phone' },
              { k: 'visit_count', label: 'Visits' },
              { k: 'total_spend', label: 'Spend', render: (r) => `${sym}${(r.total_spend || 0).toFixed(2)}` },
              { k: 'favorite_item', label: 'Favourite' },
              { k: 'days_since', label: 'Days since last' },
              { k: 'suggested_offer', label: 'AI Suggested Offer' },
            ]}
            fetcher={() => axios.get(`${API_URL}/api/ai/customer-retention?outlet_id=${oid}`, { withCredentials: true }).then((r) => r.data)}
            deps={[oid]} />
        </TabsContent>
        <TabsContent value="inventory">
          <TableTab icon={Package} title="Inventory Prediction" accent="data" testId="tbl-inv"
            desc="Tomorrow's predicted consumption."
            columns={[
              { k: 'ingredient', label: 'Ingredient' },
              { k: 'current_stock', label: 'In stock' },
              { k: 'predicted_demand_tomorrow', label: 'Predicted use' },
              { k: 'shortage_risk', label: 'Risk' , render: (r) => <Badge tone={r.shortage_risk === 'high' ? 'risk' : r.shortage_risk === 'medium' ? 'growth' : 'data'}>{r.shortage_risk}</Badge> },
              { k: 'suggested_purchase_qty', label: 'Buy qty' },
              { k: 'days_until_stockout', label: 'Days left' },
            ]}
            fetcher={() => axios.get(`${API_URL}/api/ai/inventory-prediction?outlet_id=${oid}`, { withCredentials: true }).then((r) => r.data)}
            deps={[oid]} />
        </TabsContent>
        <TabsContent value="staff">
          <TableTab icon={UserCog} title="Staff Performance" accent="growth" testId="tbl-staff"
            desc="Order volume and ticket size per team member."
            columns={[
              { k: 'staff', label: 'Staff' },
              { k: 'orders_handled', label: 'Orders' },
              { k: 'avg_ticket', label: 'Avg ticket', render: (r) => `${sym}${(r.avg_ticket || 0).toFixed(2)}` },
              { k: 'upsell_pct', label: 'Upsell %' },
              { k: 'comment', label: 'AI comment' },
            ]}
            fetcher={() => axios.get(`${API_URL}/api/ai/staff-performance?outlet_id=${oid}`, { withCredentials: true }).then((r) => r.data)}
            deps={[oid]} />
        </TabsContent>
        <TabsContent value="profit">
          <TableTab icon={TrendingUp} title="Profit Optimization" accent="profit" testId="tbl-profit"
            desc="Compare sales × profit for every item."
            columns={[
              { k: 'name', label: 'Item' },
              { k: 'sales_count', label: 'Sales' },
              { k: 'revenue', label: 'Revenue', render: (r) => `${sym}${(r.revenue || 0).toFixed(2)}` },
              { k: 'profit', label: 'Profit', render: (r) => `${sym}${(r.profit || 0).toFixed(2)}` },
              { k: 'margin_pct', label: 'Margin %' },
              { k: 'ai_action', label: 'AI Action' },
            ]}
            fetcher={() => axios.get(`${API_URL}/api/ai/profit-optimization?outlet_id=${oid}`, { withCredentials: true }).then((r) => r.data)}
            deps={[oid]} />
        </TabsContent>
        <TabsContent value="settings"><AIAdminSettings outletId={oid} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default AIGrowthPage;
